import {
  Alert,
  Card,
  Group,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertCircle,
  IconBolt,
  IconDeviceMobile,
  IconHeartHandshake,
  IconInfoCircle,
  IconUserCheck,
  IconUsers,
} from "@tabler/icons-react";

import {
  useGetAppUsage,
  useGetInsightsAnalysis,
  useGetSummary,
} from "../../api/insights";

const formatPercent = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return `${(n * 100).toFixed(1)}%`;
};

const engagementGrade = (score) => {
  if (score >= 70) return { label: "Strong", color: "green" };
  if (score >= 40) return { label: "Moderate", color: "yellow" };
  return { label: "Low", color: "orange" };
};

function KpiCard({ icon, iconColor = "blue", label, value, color, tooltip }) {
  return (
    <Card withBorder padding="lg" radius="md">
      <Group gap="md" wrap="nowrap" align="center">
        <ThemeIcon
          className="insights-kpi-icon"
          variant="light"
          color={iconColor}
          size={44}
          radius="md"
        >
          {icon}
        </ThemeIcon>
        <Stack gap={2} style={{ minWidth: 0, flex: 1 }}>
          <Group gap={6} align="center" wrap="nowrap">
            <Text size="xs" c="dimmed" fw={600} tt="uppercase" lh={1.3}>
              {label}
            </Text>
            {tooltip && (
              <Tooltip label={tooltip} multiline w={280} withArrow>
                <IconInfoCircle
                  size={14}
                  style={{
                    color: "var(--mantine-color-dimmed)",
                    flexShrink: 0,
                  }}
                />
              </Tooltip>
            )}
          </Group>
          <Title className="insights-kpi-value" order={2} c={color}>
            {value}
          </Title>
        </Stack>
      </Group>
    </Card>
  );
}

const ENGAGEMENT_INDEX_TOOLTIP =
  "Composite score (0–100) of how actively attendees used the app: agenda, sessions, features, sponsors, and polls/surveys. " +
  "Low (0–39): minimal use. Moderate (40–69): active use for the basics. Strong (70–100): consistent use across the app.";

function EventSummary({ eventId }) {
  const { data, isPending, isError, error } = useGetSummary(eventId);
  const { data: appUsage, isPending: appUsagePending } =
    useGetAppUsage(eventId);
  const { data: analysis } = useGetInsightsAnalysis(eventId);

  const isOn = (key) => analysis?.sections?.[key]?.isEnabled !== false;
  const grade = engagementGrade(data?.engagementIndex ?? 0);
  const showEngagement =
    isOn("engagementIndex") && (isPending || data?.engagementIndex != null);
  const appUsageGrade = engagementGrade(appUsage?.score ?? 0);
  const showCheckInRate =
    isOn("checkInRate") && (isPending || Number(data?.checkInRate) > 0);
  const showSponsorReach =
    isOn("sponsorReach") &&
    (isPending || Number(data?.sponsorInteractionRate) > 0);

  return (
    <Stack gap="sm">
      {isError && (
        <Alert
          color="red"
          icon={<IconAlertCircle size={16} />}
          title="Failed to load summary"
        >
          {error?.message || "Could not load the event summary."}
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="md">
        {isOn("registeredAttendees") && (
          <KpiCard
            icon={<IconUsers size={24} />}
            iconColor="blue"
            label="Registered attendees"
            value={
              isPending
                ? "—"
                : (data?.totalRegisteredAttendees ?? 0).toLocaleString()
            }
          />
        )}
        {showCheckInRate && (
          <KpiCard
            icon={<IconUserCheck size={24} />}
            iconColor="teal"
            label="Check-in rate"
            value={isPending ? "—" : formatPercent(data?.checkInRate)}
          />
        )}
        {showEngagement && (
          <KpiCard
            icon={<IconBolt size={24} />}
            iconColor={grade.color}
            label={`Engagement index (${grade.label})`}
            value={isPending ? "—" : `${data.engagementIndex}/100`}
            color={grade.color}
            tooltip={ENGAGEMENT_INDEX_TOOLTIP}
          />
        )}
        {isOn("appFeatureUsage") && (
          <KpiCard
            icon={<IconDeviceMobile size={24} />}
            iconColor={appUsageGrade.color}
            label="App feature usage"
            value={
              appUsagePending || appUsage?.score == null
                ? "—"
                : `${appUsage.score}%`
            }
            color={appUsageGrade.color}
          />
        )}
        {showSponsorReach && (
          <KpiCard
            icon={<IconHeartHandshake size={24} />}
            iconColor="grape"
            label="Sponsor reach"
            value={
              isPending ? "—" : formatPercent(data?.sponsorInteractionRate)
            }
          />
        )}
      </SimpleGrid>
    </Stack>
  );
}

export default EventSummary;
